import { useEffect, useState } from 'react';

export type DeviceType = 'mobile' | 'ipad' | 'desktop';

export const useMobileOptimizations = () => {
  const [isMobile, setIsMobile] = useState(false);
  const [isTablet, setIsTablet] = useState(false);
  const [isDesktop, setIsDesktop] = useState(true);
  const [deviceType, setDeviceType] = useState<DeviceType>('desktop');
  const [isTouchDevice, setIsTouchDevice] = useState(false);

  useEffect(() => {
    const checkDevice = () => {
      const width = window.innerWidth;
      const height = window.innerHeight;

      // Detecção de iPad (considera user agent também)
      const isIPadUserAgent = /iPad|Macintosh/.test(navigator.userAgent) && navigator.maxTouchPoints > 0;
      const isIPadWidth = width >= 768 && width < 1024;
      const isIPad = isIPadWidth || isIPadUserAgent;

      const mobile = width < 768;
      const tablet = width >= 768 && width < 1024;
      const desktop = width >= 1024;

      setIsMobile(mobile && !isIPad);
      setIsTablet(tablet || isIPad);
      setIsDesktop(desktop && !isIPad);

      // Determinar deviceType isolado
      if (mobile && !isIPad) {
        setDeviceType('mobile');
      } else if (tablet || isIPad) {
        setDeviceType('ipad');
      } else {
        setDeviceType('desktop');
      }

      setIsTouchDevice('ontouchstart' in window || navigator.maxTouchPoints > 0);
    };

    checkDevice();
    window.addEventListener('resize', checkDevice);
    window.addEventListener('orientationchange', checkDevice);

    // Fix for mobile viewport height
    const setVH = () => {
      const vh = window.innerHeight * 0.01;
      document.documentElement.style.setProperty('--vh', `${vh}px`);
    };

    setVH();
    window.addEventListener('resize', setVH);
    window.addEventListener('orientationchange', setVH);

    return () => {
      window.removeEventListener('resize', checkDevice);
      window.removeEventListener('orientationchange', checkDevice);
      window.removeEventListener('resize', setVH);
      window.removeEventListener('orientationchange', setVH);
    };
  }, []);

  // Haptic feedback simulation
  const hapticFeedback = (type: 'light' | 'medium' | 'heavy' = 'light') => {
    if (isTouchDevice) {
      // Try to use native haptic feedback if available
      if ('vibrate' in navigator) {
        const patterns = {
          light: [10],
          medium: [20],
          heavy: [30]
        };
        navigator.vibrate(patterns[type]);
      }

      // Fallback to visual feedback
      const element = document.activeElement as HTMLElement;
      if (element) {
        element.classList.add(`haptic-${type}`);
        setTimeout(() => {
          element.classList.remove(`haptic-${type}`);
        }, 200);
      }
    }
  };

  // Prevent zoom on input focus (iOS)
  const preventZoom = () => {
    if (isMobile && /iPad|iPhone|iPod/.test(navigator.userAgent)) {
      const viewport = document.querySelector('meta[name="viewport"]');
      if (viewport) {
        viewport.setAttribute('content',
          'width=device-width, initial-scale=1.0, maximum-scale=1.0, user-scalable=no'
        );
      }
    }
  };

  // Restore zoom after input blur
  const restoreZoom = () => {
    if (isMobile && /iPad|iPhone|iPod/.test(navigator.userAgent)) {
      const viewport = document.querySelector('meta[name="viewport"]');
      if (viewport) {
        viewport.setAttribute('content',
          'width=device-width, initial-scale=1.0, user-scalable=yes'
        );
      }
    }
  };

  // Optimize for mobile keyboard
  const handleKeyboardOpen = () => {
    if (isMobile) {
      document.body.style.height = `${window.innerHeight}px`;
    }
  };

  const handleKeyboardClose = () => {
    if (isMobile) {
      document.body.style.height = 'auto';
    }
  };

  return {
    isMobile,
    isTablet,
    isDesktop,
    deviceType,
    isTouchDevice,
    viewportHeight: typeof window !== 'undefined' ? window.innerHeight : 0,
    hapticFeedback,
    preventZoom,
    restoreZoom,
    handleKeyboardOpen,
    handleKeyboardClose
  };
};
